/**
 * BodyChartAnatomical — 2D muscle map (react-body-highlighter).
 * Iter 130 (Feb, 2026) — Fixed left/right mirroring bug (data + VISUAL).
 *
 * The underlying library keys every bilateral muscle group under a single
 * slug (e.g. `biceps`), so its native `onClick` callback tells you WHICH
 * muscle was clicked but never WHICH SIDE. Result: tapping the right biceps
 * would also register a mirrored marker on the left AND both polygons would
 * light up with the highlight colour.
 *
 * Fix strategy:
 *   1. Data layer — a capture-phase click handler reads the actual
 *      <polygon> tapped, derives its patient-side from bbox coords, and
 *      the marker is stored with a `side` field so left/right are separate.
 *   2. Visual layer — a `useLayoutEffect` runs after every render and walks
 *      every polygon in the SVG. For each polygon we compute its patient-
 *      side from its bbox, then check `painPoints` for a marker matching
 *      THIS muscle + THIS side. Polygons that were painted highlighted by
 *      the library but shouldn't be (wrong side) get their fill reset to
 *      the neutral body colour.
 *
 * Because the library doesn't tag polygons with data-muscle attributes, we
 * hard-code the polygon-order table below. It matches the library's
 * anterior (`c`) and posterior (`i`) arrays byte-for-byte — v6.1.0 as of
 * Feb 2026. If the library is ever bumped, re-verify this table.
 *
 * Props (unchanged shape — drop-in compat):
 *   painPoints      Array<{ view, muscle, side?, intensity, type?, note?, x?, y? }>
 *   onAdd           (point) => void
 *   onRemove        (point) => void
 *   readOnly        bool
 *   showIntensity   bool  — Iter 133. When false (default) the patient-facing
 *                          intensity slider is hidden and every new marker
 *                          gets intensity=5 as a placeholder for the
 *                          clinician to grade properly in Part 2.
 */
import React, { useMemo, useState, useRef, useLayoutEffect } from 'react';
import Model from 'react-body-highlighter';

const INTENSITY_COLORS = {
  1: '#FEF3C7', 2: '#FCD34D', 3: '#FBBF24',
  4: '#F97316', 5: '#EA580C', 6: '#DC2626',
  7: '#B91C1C', 8: '#991B1B', 9: '#7F1D1D', 10: '#7F1D1D',
};

// Default body colour used by the library when a polygon has no highlight.
const BODY_DEFAULT_FILL = '#B6BDC3';

const MUSCLE_LABELS = {
  trapezius: 'Trapezius', neck: 'Neck',
  chest: 'Chest', biceps: 'Biceps', triceps: 'Triceps',
  forearm: 'Forearm', back: 'Upper back', 'upper-back': 'Upper back',
  'lower-back': 'Lower back', 'front-deltoids': 'Front deltoid',
  'back-deltoids': 'Rear deltoid', abs: 'Abs', obliques: 'Obliques',
  adductor: 'Adductor', adductors: 'Adductors', abductors: 'Abductors',
  hamstring: 'Hamstring', quadriceps: 'Quadriceps',
  calves: 'Calves', gluteal: 'Gluteal', glutes: 'Gluteal',
  knees: 'Knees', hands: 'Hands', feet: 'Feet', head: 'Head',
  'left-soleus': 'Left soleus', 'right-soleus': 'Right soleus',
};

// Muscles that are anatomically midline (single structure) — don't tag
// them with a side. Everything else in the library is bilateral.
//
// Iter 131 (Feb 2026) — trapezius, upper-back, lower-back and neck were
// PREVIOUSLY treated as midline, but the underlying library actually
// renders TWO polygons per side for each of them. That caused clicking
// one side to light BOTH — the exact "when choosing one muscle the other
// automatically is selected" bug users reported. They're now bilateral.
//
// `left-soleus` / `right-soleus` are already pre-split by the library
// itself (side is encoded in the muscle slug) — keeping them midline
// means the pendingSide detection doesn't fight the slug.
const MIDLINE_MUSCLES = new Set([
  'head',
  'left-soleus', 'right-soleus',
]);

// Iter 130 — polygon-index → muscle-slug map. Mirrors the render order of
// the arrays `c` (anterior) and `i` (posterior) in the library source
// (react-body-highlighter@6.1.0). Each entry is [slug, polygon-count].
const ANTERIOR_MUSCLE_ORDER = [
  ['chest', 2], ['obliques', 2], ['abs', 2], ['biceps', 2],
  ['triceps', 2], ['neck', 2], ['front-deltoids', 2], ['head', 1],
  ['abductors', 2], ['quadriceps', 6], ['knees', 2], ['calves', 4],
  ['forearm', 4],
];
const POSTERIOR_MUSCLE_ORDER = [
  ['head', 1], ['trapezius', 2], ['back-deltoids', 2], ['upper-back', 2],
  ['triceps', 4], ['lower-back', 2], ['forearm', 4], ['gluteal', 2],
  ['adductor', 2], ['hamstring', 4], ['knees', 2], ['calves', 4],
  ['left-soleus', 1], ['right-soleus', 1],
];
function flattenOrder(order) {
  const out = [];
  for (const [muscle, count] of order) {
    for (let i = 0; i < count; i++) out.push(muscle);
  }
  return out;
}
const ANTERIOR_POLY_MUSCLE = flattenOrder(ANTERIOR_MUSCLE_ORDER);
const POSTERIOR_POLY_MUSCLE = flattenOrder(POSTERIOR_MUSCLE_ORDER);

export default function BodyChartAnatomical({ painPoints = [], onAdd, onRemove, readOnly = false, showIntensity = false }) {
  const [view, setView] = useState('anterior');  // 'anterior' | 'posterior'
  const [intensity, setIntensity] = useState(5);

  // Iter 131 — derive a plain string so the JSX doesn't rely on a ternary
  // inside an `x-dynamic` element (Emergent's editor runtime otherwise
  // caches the first branch and never re-renders the label on view flip).
  const viewLabel = view === 'anterior' ? 'Anterior · Muscles' : 'Posterior · Muscles';

  // Iter 130 — populated from the capture-phase click handler; consumed by
  // the library's onClick handler that fires immediately after. Ref because
  // we don't want React re-renders between the two events.
  const pendingSideRef = useRef(null);
  const wrapRef = useRef(null);

  // Translate painPoints into the `data` prop the library expects.
  // Merge across sides so the paint intensity reflects the most severe of
  // left/right when both are recorded.
  const heatData = useMemo(() => {
    const byMuscle = {};
    painPoints.forEach(p => {
      if (!p.muscle) return;
      byMuscle[p.muscle] = Math.max(byMuscle[p.muscle] || 0, p.intensity || 5);
    });
    return Object.entries(byMuscle).map(([m, i]) => ({
      name: MUSCLE_LABELS[m] || m,
      muscles: [m],
      frequency: i,
    }));
  }, [painPoints]);

  /**
   * Iter 130 — Post-render polygon fill cleanup. Walks every <polygon> in
   * the SVG, computes its patient-side from bbox coords, and — if the
   * library painted it as highlighted despite no matching marker for THIS
   * side — resets the fill to the neutral body colour.
   *
   * Runs synchronously after every commit that could have changed the
   * paint (painPoints or view flip) so users never see a mirrored flash.
   */
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const polygons = wrap.querySelectorAll('svg.rbh polygon');
    if (!polygons.length) return;
    const map = view === 'anterior' ? ANTERIOR_POLY_MUSCLE : POSTERIOR_POLY_MUSCLE;
    // Determine SVG midline once (all polygons share the parent SVG).
    const svg = polygons[0].ownerSVGElement;
    let svgCenterX = 50; // library viewBox is "0 0 100 200"
    if (svg?.viewBox?.baseVal?.width) {
      svgCenterX = svg.viewBox.baseVal.x + svg.viewBox.baseVal.width / 2;
    }

    polygons.forEach((poly, idx) => {
      const muscle = map[idx];
      if (!muscle) return;
      poly.dataset.muscle = muscle;
      // Compute this polygon's patient-side.
      let patientSide;
      if (MIDLINE_MUSCLES.has(muscle)) {
        patientSide = 'mid';
      } else {
        let cx = 50;
        try {
          const bb = poly.getBBox();
          cx = bb.x + bb.width / 2;
        } catch (_) { /* ignore */ }
        const viewerSide = cx < svgCenterX ? 'left' : 'right';
        patientSide = view === 'anterior'
          ? (viewerSide === 'left' ? 'right' : 'left')
          : viewerSide;
      }
      poly.dataset.patientSide = patientSide;

      // Should this polygon stay highlighted? Compute the highest matching
      // marker intensity so multi-marker (e.g. 5 + 8) polygons show the
      // most severe colour, matching the library's internal max-behaviour.
      const relevantMarkers = painPoints.filter(p => (
        p.muscle === muscle
        && (MIDLINE_MUSCLES.has(muscle) || (p.side || null) === patientSide)
      ));
      // Force-write the fill directly (bypassing React's diffing so a stale
      // React-computed style can't leak the wrong side's colour).
      if (relevantMarkers.length > 0) {
        const maxIntensity = relevantMarkers.reduce(
          (m, r) => Math.max(m, r.intensity || 5), 0,
        );
        poly.style.fill = INTENSITY_COLORS[maxIntensity] || INTENSITY_COLORS[5];
      } else {
        poly.style.fill = BODY_DEFAULT_FILL;
      }
    });
  }, [painPoints, view]);

  /**
   * Iter 130 — Capture-phase click handler. Runs BEFORE the library's
   * onClick fires, giving us access to the actual DOM polygon that was
   * tapped. We derive left/right from its position inside the SVG
   * viewport and stash the result in a ref for the muscle-click handler.
   */
  const handleClickCapture = (e) => {
    if (readOnly) return;
    const el = e.target;
    if (!el || el.tagName?.toLowerCase() !== 'polygon') {
      pendingSideRef.current = null;
      return;
    }
    let polyCenterX;
    try {
      const bbox = el.getBBox();
      polyCenterX = bbox.x + bbox.width / 2;
    } catch (_) {
      pendingSideRef.current = null;
      return;
    }
    const svg = el.ownerSVGElement;
    // Compute the SVG midline. Prefer the viewBox; fall back to the raw
    // bounding rect width if the viewBox isn't set.
    let svgCenterX;
    if (svg?.viewBox?.baseVal?.width) {
      svgCenterX = svg.viewBox.baseVal.x + svg.viewBox.baseVal.width / 2;
    } else if (svg) {
      svgCenterX = svg.getBBox().x + svg.getBBox().width / 2;
    } else {
      svgCenterX = 50;
    }
    const viewerSide = polyCenterX < svgCenterX ? 'left' : 'right';
    // Anterior view (person facing viewer): their RIGHT arm is on OUR LEFT.
    // Posterior view (looking at their back): their RIGHT is on OUR RIGHT.
    const patientSide = view === 'anterior'
      ? (viewerSide === 'left' ? 'right' : 'left')
      : viewerSide;
    pendingSideRef.current = patientSide;
  };

  /**
   * True toggle: tapping the same muscle+side a second time removes the
   * marker. Left biceps and right biceps are treated as independent
   * markers so a patient can have pain on one side only.
   */
  const onMuscleClick = (data) => {
    if (readOnly || !onAdd) return;
    const muscleSlug = data?.muscle || data?.muscles?.[0];
    if (!muscleSlug) return;
    const side = MIDLINE_MUSCLES.has(muscleSlug) ? null : (pendingSideRef.current || 'right');
    pendingSideRef.current = null;
    // Match by muscle AND side — right biceps and left biceps are different.
    const existing = painPoints.find(
      p => p.muscle === muscleSlug && (p.side || null) === side
    );
    if (existing) {
      onRemove?.(existing);
      return;
    }
    onAdd({
      view: view === 'anterior' ? 'front' : 'back',
      muscle: muscleSlug,
      side,
      intensity,
      type: 'pain',
      note: '',
      x: 50, y: 60,
    });
  };

  const labelFor = (p) => {
    const base = MUSCLE_LABELS[p.muscle] || p.muscle;
    if (!p.side) return base;
    return `${p.side === 'left' ? 'Left' : 'Right'} ${base.toLowerCase()}`;
  };

  return (
    <div className="flex flex-col items-center" data-testid="body-chart-anatomical">
      {/* Front / Back toggle */}
      <div className="flex gap-2 mb-3 print:hidden">
        <button type="button" onClick={() => setView('anterior')} data-testid="body-anterior"
                className={`px-3 py-1.5 text-xs rounded-md transition ${view === 'anterior' ? 'bg-brand text-white' : 'border border-soft text-fg-muted hover:border-brand'}`}>
          Front
        </button>
        <button type="button" onClick={() => setView('posterior')} data-testid="body-posterior"
                className={`px-3 py-1.5 text-xs rounded-md transition ${view === 'posterior' ? 'bg-brand text-white' : 'border border-soft text-fg-muted hover:border-brand'}`}>
          Back
        </button>
      </div>

      {/* Intensity picker — chosen value applies to the next clicked muscle.
          Iter 133 — Hidden for patients (Part 1) unless the parent opts in
          with showIntensity=true (clinician Part 2). */}
      {!readOnly && showIntensity && (
        <div className="mb-3 w-full max-w-[260px] print:hidden" data-testid="body-intensity-picker">
          <div className="flex items-center justify-between text-[10px] uppercase tracking-wider text-fg-muted mb-1">
            <span>VAS for next marker</span>
            <span className="font-bold text-fg" style={{ color: INTENSITY_COLORS[intensity] }}>{intensity}/10</span>
          </div>
          <input type="range" min="1" max="10" value={intensity}
                 onChange={(e) => setIntensity(parseInt(e.target.value))}
                 className="w-full accent-brand"/>
        </div>
      )}

      {/* Iter 130 — capture-phase click listener sniffs the polygon before
          the library's own onClick, so we can tag left vs right. The wrap
          ref is also used by the useLayoutEffect above to walk polygons
          and unpaint the mirrored highlight. */}
      <div
        ref={wrapRef}
        className="surface border border-soft rounded-lg p-4 max-w-[260px]"
        onClickCapture={handleClickCapture}
        data-testid="body-svg-wrap"
      >
        <div key={view} className="text-xs uppercase tracking-[0.2em] text-fg-muted mb-2 text-center">
          {viewLabel}
        </div>
        <Model
          data={heatData}
          type={view}
          highlightedColors={[
            INTENSITY_COLORS[2], INTENSITY_COLORS[4], INTENSITY_COLORS[6],
            INTENSITY_COLORS[8], INTENSITY_COLORS[10],
          ]}
          onClick={onMuscleClick}
          style={{ width: '220px', cursor: readOnly ? 'default' : 'pointer' }}
        />
        <div data-testid="body-react-highlighter" aria-hidden="true" style={{ display: 'none' }}/>
        {!readOnly && (
          <div className="text-[11px] text-fg-muted text-center mt-3 space-y-0.5" data-testid="body-help">
            <div>Tap a muscle to add a marker · Tap it again to remove.</div>
            <div className="text-[10px] italic">
              Left &amp; right are tracked independently — only the side you
              tap will highlight.
            </div>
          </div>
        )}
        {readOnly && painPoints.length > 0 && (
          <div className="mt-3 text-[11px] text-fg-secondary text-center">
            {painPoints.filter(p => p.muscle).length} muscle marker(s) recorded.
          </div>
        )}
      </div>

      {/* Marker list with quick-remove — side-aware labels */}
      {!readOnly && painPoints.filter(p => p.muscle).length > 0 && (
        <div className="mt-3 w-full max-w-[260px] space-y-1.5" data-testid="muscle-marker-list">
          {painPoints.filter(p => p.muscle).map((p, i) => (
            <div key={`${p.muscle}-${p.side || 'mid'}-${i}`} className="flex items-center gap-2 text-xs">
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: INTENSITY_COLORS[p.intensity || 5] }}/>
              <span className="flex-1 truncate">{labelFor(p)}</span>
              <span className="text-fg-muted">VAS {p.intensity || 5}</span>
              <button onClick={() => onRemove?.(p)} className="text-[#B83A3A] hover:opacity-70" aria-label="Remove marker">×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
